/**
 * Cart Event Bridge V4
 * 
 * 职责简化:
 * - 监听 Theme → React 的 cart:changed 事件
 * - 触发 requestCartRefresh() 而不是直接 setCart
 * - 向 Theme 广播 React 的购物车更新
 */

import { requestCartRefresh } from '../bootstrap/refreshCoordinator';
import { emitToast } from './error/errorEventBus';
import { parseStockError } from '../utils/cartRules';
import { ErrorCode } from '../errors/catalog';
import { GlobalErrorHandler } from '../errors';

type CartOrigin = 'theme' | 'react' | 'network-interceptor' | 'init' | 'trigger';

interface CartChangedEventDetail {
  cart: any;
  origin: CartOrigin;
}

declare global {
  interface Window {
    notifyCartUpdate?: (cart: any, origin?: CartOrigin) => void;
    refreshCartData?: (origin?: CartOrigin | 'init') => void;
  }
}

export class CartEventBridge {
  private static isInitialized = false;

  /**
   * V4: 初始化事件桥
   */
  public static init() {
    if (this.isInitialized) return;
    if (typeof window === 'undefined') return;

    // 监听 Theme → React 事件
    window.addEventListener('cart:changed', this.handleCartChanged);
    window.addEventListener('cart:toast', this.handleCartToast);

    // 暴露全局 refreshCartData 供 nativeInterceptor 调用
    window.refreshCartData = this.handleRefreshRequest;

    this.isInitialized = true;
  }

  /**
   * V4: Theme → React 事件处理
   * 不再直接 setCart,改为触发 requestCartRefresh
   */
  private static handleCartChanged = (event: Event) => {
    const detail = (event as CustomEvent<CartChangedEventDetail>).detail;
    if (!detail?.cart) {
      console.warn('[CartEventBridge] cart:changed event missing cart payload');
      return;
    }

    console.log('[CartEventBridge] Received cart:changed from theme, triggering refresh');

    // V4: 统一通过 requestCartRefresh 更新
    void requestCartRefresh('theme:cart:changed', { showLoading: false });
  };

  /**
   * Theme → React toast bridge
   */
  private static handleCartToast = (event: Event) => {
    const detail = (event as CustomEvent<{ type?: 'success' | 'error' | 'info'; message?: string }>).detail;
    if (!detail?.message) return;

    if (detail.type === 'error') {
      const parsed = parseStockError(detail.message);
      if (parsed.isStockError) {
        GlobalErrorHandler.handle({
          code: ErrorCode.CART_STOCK_LIMIT,
          message: detail.message,
          level: 'warning',
          retryable: false,
          timestamp: Date.now(),
          context: {
            domain: 'cart',
            operation: 'add',
            maxQty: parsed.maxQty
          }
        }, {
          scope: 'cartDrawer',
          source: 'addToCart',
          userInitiated: true
        });
        return;
      }
    }

    emitToast({
      type: detail.type || 'info',
      message: detail.message
    });
  };

  /**
   * V4: 处理 refresh 请求 (from nativeInterceptor)
   */
  private static handleRefreshRequest = (origin?: CartOrigin | 'init') => {
    console.log(`[CartEventBridge] refreshCartData called from ${origin || 'unknown'}`);
    void requestCartRefresh(`refresh:${origin || 'unknown'}`, { showLoading: false });
  };

  /**
   * React → Theme 通知
   * 向 Theme 广播购物车更新
   */
  public static notifyTheme(cart: any) {
    if (typeof window.notifyCartUpdate === 'function') {
      window.notifyCartUpdate(cart, 'react');
    }

    // 触发自定义事件供主题监听
    window.dispatchEvent(
      new CustomEvent('cart:changed', {
        detail: { cart, origin: 'react' }
      })
    );
  }
}
